import { useRef, useEffect } from 'react';

const SIZES = {
  xs: 'w-6 h-6',
  sm: 'w-10 h-10',
  md: 'w-16 h-16',
  lg: 'w-24 h-24 sm:w-28 sm:h-28',
};

const ICON_SIZES = {
  xs: 'w-4 h-4',
  sm: 'w-5 h-5',
  md: 'w-7 h-7',
  lg: 'w-10 h-10 sm:w-12 sm:h-12',
};

// A speaker button that plays an audio clip. Click to play (or replay from the
// start). When `autoPlay` is set it plays once on mount / when the src changes —
// used for audio prompts, where playback should start automatically. Autoplay
// rejections (e.g. before any user interaction) are ignored silently.
export default function AudioButton({ src, autoPlay = false, size = 'md', label = 'Play audio', className = '' }) {
  const audioRef = useRef(null);

  useEffect(() => {
    if (!src) return undefined;
    const audio = new Audio(src);
    audioRef.current = audio;
    if (autoPlay) audio.play().catch(() => {});
    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, [src, autoPlay]);

  function play() {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }

  return (
    <button
      type="button"
      onClick={play}
      disabled={!src}
      aria-label={label}
      className={`flex items-center justify-center rounded-full bg-amber-100 hover:bg-amber-200 active:bg-amber-300 text-amber-700 transition disabled:opacity-40 disabled:cursor-not-allowed ${SIZES[size]} ${className}`}
    >
      {/* Speaker icon */}
      <svg className={ICON_SIZES[size]} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
      </svg>
    </button>
  );
}
